import type { AfterViewInit, OnDestroy } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { FaceFilterService } from '@freelancer/face-filter';
import { VideoComponent } from '@freelancer/ui/video';

@Component({
  standalone: false,
  selector: 'fl-face-filter-tessellation',
  template: `
    <canvas
      #faceFilterTessellationCanvas
      class="Overlay"
      [class.MirrorMode]="mirrorMode"
    ></canvas>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./face-filter-tessellation.component.scss'],
})
export class FaceFilterTessellationComponent
  implements AfterViewInit, OnDestroy
{
  private changeDetectorRef = inject(ChangeDetectorRef);
  private faceFilterService = inject(FaceFilterService);

  @Input()
  activeRecordingElementRef: VideoComponent;

  @Input()
  mirrorMode = false;

  @Output()
  faceFilterLoaded = new EventEmitter<void>();

  @ViewChild('faceFilterTessellationCanvas')
  faceFilterCanvasRef: ElementRef<HTMLCanvasElement>;

  animationRequestId: number;
  canvasContext: CanvasRenderingContext2D | null;

  async ngAfterViewInit(): Promise<void> {
    this.faceFilterCanvasRef.nativeElement.width =
      this.activeRecordingElementRef.media.nativeElement.offsetWidth;
    this.faceFilterCanvasRef.nativeElement.height =
      this.activeRecordingElementRef.media.nativeElement.offsetHeight - 30;

    await this.faceFilterService.initiateFaceLandmarker();

    this.faceFilterLoaded.emit();

    this.canvasContext =
      this.faceFilterCanvasRef.nativeElement.getContext('2d');

    this.trackFace();

    this.changeDetectorRef.markForCheck();
  }

  private trackFace(): void {
    const results = this.faceFilterService.detectForVideo(
      this.activeRecordingElementRef.media.nativeElement,
    );

    if (!results) {
      return;
    }

    if (!this.canvasContext) {
      return;
    }

    this.canvasContext.clearRect(
      0,
      0,
      this.faceFilterCanvasRef.nativeElement.width,
      this.faceFilterCanvasRef.nativeElement.height,
    );

    this.faceFilterService.drawTessellation(this.canvasContext, results);

    this.animationRequestId = requestAnimationFrame(this.trackFace.bind(this));
  }

  ngOnDestroy(): void {
    if (this.animationRequestId) {
      cancelAnimationFrame(this.animationRequestId);
    }
  }
}
